import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { SelectField, TextField } from "@/components/Field";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { usePeople } from "@/lib/hooks/usePeople";
import { useAuth } from "@/contexts/AuthContext";
import { call, CALLABLES } from "@/services/callables";
import { AGE_BAND_CONSEQUENCE_KEY, ageBandConsequence } from "./rules";
import { SELECTABLE_AGE_BANDS } from "../people/review";
import { ownerMarkKey, teachingStaff } from "../people/staff";
import type { AgeBand, Group, SessionMode } from "@/types";
import { collator as collatorFor } from "@/i18n";

/**
 * A new group.
 *
 * The age band is the consequential field, so the consequence is shown at the
 * point of choice rather than discovered later: a group with anyone under 18 in
 * it needs parental consent on file before a lesson can start. Reading that
 * while picking the band is the difference between a rule and an obstacle.
 */
export function NewGroupPage() {
  const t = useT();
  const navigate = useNavigate();
  const { success, showError } = useToast();
  const { people } = usePeople();
  const { institution, user } = useAuth();
  const { locale } = useI18n();

  const [name, setName] = useState("");
  const [ageBand, setAgeBand] = useState<AgeBand>("13-15");
  const [defaultMode, setDefaultMode] = useState<SessionMode>("online");
  const [teacherUids, setTeacherUids] = useState<string[]>([]);
  const [rosterUids, setRosterUids] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  /*
    Both pickers are in the reader's alphabet, not in storage order — which is
    what they were, while the roster beside them was alphabetical (live run,
    2026-09-09).
  */
  const collator = useMemo(
    () => collatorFor(locale),
    [locale]
  );

  // The owner is offered as a teacher, marked: they host lessons too.
  const teachers = useMemo(
    () =>
      teachingStaff(people)
        .map((person) => ({
          uid: person.uid,
          displayName: person.displayName,
          markKey: ownerMarkKey(person, user?.uid),
        }))
        .sort((a, b) => collator.compare(a.displayName, b.displayName)),
    [people, user?.uid, collator]
  );
  const students = useMemo(
    () =>
      people
        .filter((person) => person.role === "student")
        .sort((a, b) => collator.compare(a.displayName, b.displayName)),
    [people, collator]
  );

  // The country decides whether a minor needs a parent to consent, so the
  // sentence under the field cannot be written from the band alone.
  const consequence = ageBandConsequence(ageBand, institution?.country ?? "");

  // What is still missing, in the order somebody fills the form in.
  const blockedKey =
    name.trim().length === 0
      ? "group.create.needsName"
      : teacherUids.length === 0
        ? "group.create.needsTeacher"
        : undefined;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { group } = await call<
        {
          name: string;
          ageBand: AgeBand;
          defaultMode: SessionMode;
          teacherUids: string[];
        },
        { group: Group }
      >(CALLABLES.createGroup, {
        name: name.trim(),
        ageBand,
        defaultMode,
        teacherUids,
      });

      if (rosterUids.length > 0) {
        await call(CALLABLES.addToRoster, { groupId: group.id, uids: rosterUids });
      }

      success("group.created", { name: name.trim() });
      navigate(`/groups/${group.id}`);
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell titleKey="group.new.title">
      <form className="flex max-w-xl flex-col gap-5" onSubmit={onSubmit}>
        <TextField
          labelKey="group.name"
          hintKey="group.nameHint"
          required
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <div>
          <SelectField
            labelKey="group.ageBand"
            value={ageBand}
            onChange={(event) => setAgeBand(event.target.value as AgeBand)}
            options={[
              ...SELECTABLE_AGE_BANDS.map((band) => ({
                value: band,
                labelKey: `import.ageBand.${band}`,
              })),
              { value: "mixed", labelKey: "import.ageBand.mixed" },
            ]}
          />
          <p
            className={`mt-2 rounded-card px-4 py-3 text-sm ${
              consequence === "minorsConsent"
                ? "border border-warning bg-warning-soft text-ink-soft"
                : "text-ink-muted"
            }`}
          >
            {t(AGE_BAND_CONSEQUENCE_KEY[consequence])}
          </p>
        </div>

        <SelectField
          labelKey="group.defaultMode"
          hintKey="group.defaultModeHint"
          value={defaultMode}
          onChange={(event) => setDefaultMode(event.target.value as SessionMode)}
          options={[
            { value: "online", labelKey: "group.mode.online" },
            { value: "offline", labelKey: "group.mode.offline" },
          ]}
        />

        <PeoplePicker
          labelKey="group.teachers"
          hintKey="group.teachersHint"
          people={teachers}
          selected={teacherUids}
          onChange={setTeacherUids}
        />

        <PeoplePicker
          labelKey="group.roster"
          hintKey="group.rosterHint"
          people={students}
          selected={rosterUids}
          onChange={setRosterUids}
        />

        {/*
          A disabled button that says nothing is a dead button (live run,
          2026-09-09). It names what is missing, and the button points at the
          sentence so a screen reader reads the two together.
        */}
        <div>
          <Button
            type="submit"
            busy={busy}
            disabled={blockedKey !== undefined}
            aria-describedby={blockedKey ? "create-blocked" : undefined}
          >
            {t("group.create")}
          </Button>
          {blockedKey ? (
            <p id="create-blocked" className="mt-2 text-sm text-ink-muted">
              {t(blockedKey)}
            </p>
          ) : null}
        </div>
      </form>
    </SectionShell>
  );
}

function PeoplePicker({
  labelKey,
  hintKey,
  people,
  selected,
  onChange,
}: {
  labelKey: string;
  hintKey: string;
  people: readonly { uid: string; displayName: string; markKey?: string }[];
  selected: readonly string[];
  onChange: (uids: string[]) => void;
}) {
  const t = useT();
  return (
    <fieldset>
      <legend className="field-label">{t(labelKey)}</legend>
      <p className="mb-2 text-sm text-ink-muted">{t(hintKey)}</p>
      <div className="max-h-48 overflow-y-auto rounded-card border border-line bg-white p-3">
        {people.map((person) => (
          <label key={person.uid} className="flex items-center gap-2 py-1 text-sm text-ink">
            <input
              type="checkbox"
              checked={selected.includes(person.uid)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, person.uid]
                    : selected.filter((uid) => uid !== person.uid)
                )
              }
            />
            {person.displayName}
            {person.markKey ? (
              <span className="text-xs text-ink-muted">{t(person.markKey)}</span>
            ) : null}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
